// Display formatting helpers. All money is rounded to cents; percents come from the
// API as fractions (0.8 = 80%).

/** $1,234.56 */
export function usd(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Fraction → "80%". `digits` controls decimals (default 0). */
export function pct(fraction: number | null | undefined, digits = 0): string {
  const v = typeof fraction === "number" && Number.isFinite(fraction) ? fraction : 0;
  return `${(v * 100).toFixed(digits)}%`;
}

/** Large counts → 1.2M / 3.4K / 950. */
export function compactNum(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  return v.toLocaleString("en-US", { notation: "compact", maximumFractionDigits: 1 });
}

/** Response time in ms → "123 ms" or "—". */
export function ms(n: number | null | undefined): string {
  return typeof n === "number" && Number.isFinite(n) ? `${Math.round(n)} ms` : "—";
}

/** ISO timestamp → local short "Jun 7, 14:32" or "—". */
export function shortTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** "x time ago" from an ISO timestamp. */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** YYYY-MM → "Jun 2026". */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  const d = new Date(Date.UTC(y, m - 1, 1));
  return d.toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

/** Big counts → "1.2B" / "3.4M" / "12K" / "950" (mockup-style compaction). */
export function big(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1e9) return sign + (a / 1e9).toFixed(a >= 1e10 ? 0 : 1) + "B";
  if (a >= 1e6) return sign + (a / 1e6).toFixed(a >= 1e7 ? 0 : 1) + "M";
  if (a >= 1e3) return sign + (a / 1e3).toFixed(0) + "K";
  return sign + String(Math.round(a));
}

/** Storage value → "47 GB" / "4.3 GB" (one decimal under 10). */
export function gb(n: number | null | undefined): string {
  const v = typeof n === "number" && Number.isFinite(n) ? n : 0;
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} GB`;
}

/** Internal service id → display label. '_account' is the zone-level rollup. */
export function serviceLabel(service: string): string {
  return service === "_account" ? "zone (buildxcloud.com)" : service;
}

/** Billing-cycle position for the given moment, computed in UTC. */
export interface BillingCycle {
  /** "Mon YYYY" label for the current UTC month. */
  label: string;
  /** Day of month (1-based). */
  day: number;
  /** Total days in the current UTC month. */
  daysInMonth: number;
  /** Fraction of the cycle elapsed (0..1). */
  pctOfCycle: number;
}

export function billingCycle(now: Date): BillingCycle {
  const valid = !Number.isNaN(now.getTime());
  const d = valid ? now : new Date();
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const label = new Date(Date.UTC(y, m, 1)).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return {
    label,
    day,
    daysInMonth,
    pctOfCycle: daysInMonth > 0 ? day / daysInMonth : 0,
  };
}
